import { useState, useRef, useEffect } from 'react'
import { Lock, Users } from 'lucide-react'

interface FolderNoteEditorProps {
  onSubmit: (content: string, visibility: 'private' | 'shared') => void
  onCancel?: () => void
  initialContent?: string
  initialVisibility?: 'private' | 'shared'
  isSubmitting?: boolean
}

export default function FolderNoteEditor({
  onSubmit,
  onCancel,
  initialContent = '',
  initialVisibility = 'private',
  isSubmitting = false,
}: FolderNoteEditorProps) {
  const [content, setContent] = useState(initialContent)
  const [visibility, setVisibility] = useState<'private' | 'shared'>(initialVisibility)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    textareaRef.current?.focus()
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'Enter' && content.trim()) {
      onSubmit(content.trim(), visibility)
    }
  }

  const handleSubmit = () => {
    if (content.trim()) {
      onSubmit(content.trim(), visibility)
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-2">
      <textarea
        ref={textareaRef}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Write a note..."
        rows={3}
        className="w-full px-3 py-2 text-sm light-input resize-none outline-none"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setVisibility(visibility === 'private' ? 'shared' : 'private')}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded ${
              visibility === 'private' ? 'bg-gray-200 text-gray-600' : 'bg-blue-50 text-blue-600'
            }`}
          >
            {visibility === 'private' ? <Lock className="w-3 h-3" /> : <Users className="w-3 h-3" />}
            {visibility === 'private' ? 'Private' : 'Shared'}
          </button>
          <span className="text-xs text-gray-500">Ctrl+Enter to submit</span>
        </div>
        <div className="flex items-center gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1 text-xs border border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={!content.trim() || isSubmitting}
            className="px-3 py-1 text-xs bg-iflow-700 text-white rounded-lg hover:bg-iflow-800 disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
